<template>
  <div :class="validationContainerClass">
    <v-validate
      :rules="rules"
      :vid="name"
      v-slot="{ errors, classes }"
      :name="label"
    >
      <div class="form-group k-rtl">
        <label :for="id">
          {{ label }}
          <div v-if="isRequired" class="is-required">
            <i class="fas fa-star-of-life"></i>
          </div>
          <a v-if="content && deletable" @click="deleteMedia"><i class="fas fa-trash-alt" ></i></a>
        </label>
        <input
          type="file"
          :id="inputID"
          :name="name"
          :ref="name"
          :accept="acceptedTypes"
          @change="setMedia"
          :class="`overflow-hidden ${inputClass} ${classes.invalid ? 'is-invalid' : ''}`"
        />
        
        <!-- Image Preview -->
        <img
          v-if="preview && isImageFile && getMediaUrl"
          class="img rounded-circle circle-border m-b-md"
          :src="getMediaUrl"
          alt="تصویر"
        />
        
        <!-- Video Preview -->
        <video
          v-if="preview && isVideoFile && getMediaUrl"
          class="video-preview m-b-md"
          :src="getMediaUrl"
          controls
        ></video>

        <span class="form-text m-b-none">
          حجم {{ mediaTypeLabel }} نباید بیشتر از {{ effectiveMediaSize }} مگ باشد
        </span>
        
        <span
          class="invalid-feedback"
          :style="`display: ${errors ? 'inline-block' : 'none'}`"
          >{{ errors[0] }}</span
        >
      </div>
    </v-validate>
  </div>
</template>

<script>
import InputCommons from "./InputCommons.vue";

export default {
  extends: InputCommons,
  props: {
    iconUrl: {
      type: String,
    },
    mediaSize: {
      default: 1,
      type: Number,
    },
    // Deprecated: use mediaSize instead
    imageSize: {
      default: 1,
      type: Number,
    },
    preview: {
      default: true,
    },
    deletable: {
      default: false,
    },
    // 'image', 'video', or 'both'
    mediaType: {
      type: String,
      default: 'image',
      validator: (value) => ['image', 'video', 'both'].includes(value)
    }
  },
  data() {
    return {
      mediaData: "",
      currentFileType: null, // 'image' or 'video'
    };
  },
  mounted() {
    // Detect initial file type if iconUrl is provided
    if (this.iconUrl) {
      this.detectFileTypeFromUrl(this.iconUrl);
    }
  },
  watch: {
    iconUrl(newVal) {
      if (newVal) {
        this.detectFileTypeFromUrl(newVal);
      }
    }
  },
  computed: {
    // Support both old and new prop names
    effectiveMediaSize() {
      return this.mediaSize || this.imageSize;
    },
    
    acceptedTypes() {
      if (this.mediaType === 'image') {
        return 'image/*';
      } else if (this.mediaType === 'video') {
        return 'video/*';
      } else {
        return 'image/*,video/*';
      }
    },
    
    mediaTypeLabel() {
      if (this.mediaType === 'image') {
        return 'تصویر';
      } else if (this.mediaType === 'video') {
        return 'ویدیو';
      } else {
        return 'فایل';
      }
    },
    
    isImageFile() {
      // If no file type detected yet, assume image for backward compatibility
      if (this.currentFileType === null && this.mediaType === 'image') {
        return true;
      }
      return this.currentFileType === 'image';
    },
    
    isVideoFile() {
      return this.currentFileType === 'video';
    },
    
    getMediaUrl() {
      if (this.mediaData) {
        return this.mediaData;
      } else if (this.iconUrl) {
        return this.iconUrl;
      }
      return null;
    },
  },
  methods: {
    detectFileTypeFromUrl(url) {
      if (!url) return;
      
      const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv'];
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
      
      const lowerUrl = url.toLowerCase();
      
      if (videoExtensions.some(ext => lowerUrl.includes(ext))) {
        this.currentFileType = 'video';
      } else if (imageExtensions.some(ext => lowerUrl.includes(ext))) {
        this.currentFileType = 'image';
      } else {
        // Default based on mediaType prop
        this.currentFileType = this.mediaType === 'video' ? 'video' : 'image';
      }
    },
    
    setMedia(event) {
      let files = event.target.files;
      
      if (!files || !files[0]) return;
      
      const file = files[0];
      
      // Detect file type
      if (file.type.startsWith('image/')) {
        this.currentFileType = 'image';
      } else if (file.type.startsWith('video/')) {
        this.currentFileType = 'video';
      }
      
      // Check file size (only for images, not for videos)
      if (this.currentFileType !== 'video' && file.size / (1024 * 1024) > this.effectiveMediaSize) {
        this.$izitoast(
          "error",
          "خطا!",
          `سایز فایل نباید از ${this.effectiveMediaSize} مگابایت بیشتر باشد`,
          {
            position: "topRight",
            displayMode: 2,
          }
        );
        $("#" + event.target.id).val("");
        return;
      }
      
      // Load and preview the file
      this.loadMedia(file);
      this.content = file;
      this.handleInput(file);
    },
    
    deleteMedia() {
      this.content = 'delete';
      this.handleInput('delete');
      this.reset();
    },
    
    loadMedia(file) {
      var reader = new FileReader();
      reader.onload = (e) => {
        this.mediaData = e.target.result;
        this.$forceUpdate();
      };
      reader.readAsDataURL(file);
    },
    
    reset() {
      this.mediaData = "";
      this.currentFileType = null;

      var mediaRef = this.$refs[this.name];
      if (mediaRef) {
        mediaRef.value = "";
      }
    },
    
    // Legacy method name support
    setIcon(event) {
      this.setMedia(event);
    },
    
    deleteImg() {
      this.deleteMedia();
    },
  },
};
</script>

<style scoped>
.img {
  height: 50px;
  width: 50px;
  position: absolute;
  top: 20px;
  left: 6px;
  object-fit: contain;
  transition: all 200ms;
}

.img:hover {
  transform: scale(3);
}

img.circle-border {
  border: 4px solid #cccccc;
  border-radius: 50%;
}

.video-preview {
  max-width: 300px;
  max-height: 200px;
  margin-top: 10px;
  border: 2px solid #cccccc;
  border-radius: 8px;
  display: block;
}
</style>